import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAuth0Context } from "@/contexts/Auth0Context";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Upload,
  FileText,
  LogOut,
  User,
  CreditCard,
  Settings,
  ChevronDown,
  BookOpen,
  ClipboardCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Footer } from "@/components/layout/Footer";

export function Layout() {
  const { user: legacyUser } = useAuth();
  const {
    user: auth0User,
    logout: auth0Logout,
    isAuthenticated: auth0Auth,
  } = useAuth0Context();
  const location = useLocation();

  // Use Auth0 user if available, otherwise fall back to legacy user
  const user = auth0User || legacyUser;
  const logout = auth0Auth ? auth0Logout : useAuth().logout;

  const navigation = [
    { name: "Upload", href: "/app/upload", icon: Upload },
    { name: "Documents", href: "/app/documents", icon: FileText },
    { name: "Review", href: "/app/review", icon: ClipboardCheck },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-xl font-bold">
                SnapLading
              </Link>
              <nav className="hidden md:flex space-x-6">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center space-x-2 text-sm font-medium transition-colors hover:text-primary",
                        location.pathname === item.href
                          ? "text-primary"
                          : "text-muted-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2"
                  >
                    <User className="h-4 w-4" />
                    <span>{user?.name || user?.email}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link
                      to="/app/upload"
                      className="flex items-center space-x-2 w-full"
                    >
                      <Upload className="h-4 w-4" />
                      <span>Upload</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/app/documents"
                      className="flex items-center space-x-2 w-full"
                    >
                      <FileText className="h-4 w-4" />
                      <span>Documents</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/app/review"
                      className="flex items-center space-x-2 w-full"
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      <span>Review</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/app/profile"
                      className="flex items-center space-x-2 w-full"
                    >
                      <User className="h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/app/billing"
                      className="flex items-center space-x-2 w-full"
                    >
                      <CreditCard className="h-4 w-4" />
                      <span>Billing</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/app/settings"
                      className="flex items-center space-x-2 w-full"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      href="/docs"
                      className="flex items-center space-x-2 w-full"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span>Documentation</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="flex items-center space-x-2 text-red-600 focus:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
